import React, { useState, useEffect, useContext, useCallback } from 'react';
import moment from 'moment-timezone';
import { useNavigate } from "react-router-dom";
import AuthContext from '../../contexts/auth-context';
import { getQueuesAPI } from '../../util';

export default function OfficeLocationController({ children, locationData, locationId }) {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const currentLocation = locationData;
  const [loading, setLoading] = useState(true);
  const [regularDayCount, setRegularDayCount] = useState(0);
  const [regularDayCountOut, setRegularDayCountOut] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [currentTimestamp, setCurrentTimestamp] = useState(null);
  const [doorData, setDoorData] = useState([]);
  const [sensorData, setSensorData] = useState({});
  const [isDoorSensorActive, setIsDoorSensorActive] = useState(false);
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isToday, setIsToday] = useState(false);

  // Overview mode ('regular' | 'overview') + its lazily-fetched payload
  const [viewMode, setViewMode] = useState('regular');
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Office calendar only requires a minimum date
  useEffect(() => {
    if (locationData && !locationData.calendarMinDate) {
      setApiError({
        message: 'Location configuration error: Missing calendarMinDate',
        status: 'config_error'
      });
    }
  }, [locationData]);

  // Initialize selectedDate to the VENUE's current day, built as a floating
  // calendar day (new Date(y, m-1, d)) so the displayed/queried day is the
  // venue's day in EVERY browser timezone. The venue tz is used only to decide
  // which day "today" is at the venue — after that the browser clock is irrelevant.
  useEffect(() => {
    if (!locationData || selectedDate !== null) return;

    const timezone = locationData.timeZone || 'America/New_York';
    const todayInTz = moment.tz(timezone).format('YYYY-MM-DD'); // venue's current calendar day
    const [y, m, d] = todayInTz.split('-').map(Number);

    setSelectedDate(new Date(y, m - 1, d));
    setIsToday(true);
    setAutoFetchEnabled(true);
  }, [locationData, selectedDate]);

  // Recalculate isToday whenever selectedDate changes (compare calendar days)
  useEffect(() => {
    if (!selectedDate || !locationData) return;

    const timezone = locationData.timeZone || 'America/New_York';
    const currentDateInLocationTz = moment.tz(timezone).format('YYYY-MM-DD');
    const selectedDay = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

    const isTodayCheck = currentDateInLocationTz === selectedDay;
    setIsToday(isTodayCheck);
    setAutoFetchEnabled(isTodayCheck); // auto-refresh only when viewing today
  }, [selectedDate, locationData]);

  // Free calendar bounds: from min date through today, every day selectable
  const minDate = locationData?.calendarMinDate;
  const maxDate = moment.tz(locationData?.timeZone || 'America/New_York').format('YYYY-MM-DD');
  const enabledDates = undefined; // no enabled-date gating for offices

  // Function to fetch location data
  const fetchLocationData = useCallback(async (date = selectedDate) => {
    if (!date) return {
      regularDayCount: 0, regularDayCountOut: 0, currentCount: 0,
      currentTimestamp: null, doorData: [], sensorData: {}, isDoorSensorActive: false
    };

    try {
      setApiError(null);

      const formattedDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

      return new Promise((resolve) => {
        getQueuesAPI("get-enterprise-office-daily-data",
          {
            timezone: currentLocation.timeZone,
            org: authCtx.org,
            locationId: currentLocation.id,
            measurement: currentLocation.measurement,
            doorMeasurement: currentLocation.doorMeasurement,
            doorCountSensorIds: currentLocation.doorCounterSensorIds,
            doorCounterSensorInfo: currentLocation.doorCounterSensorInfo,
            date: formattedDate
          },
          (data, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
              }
              console.error('Failed to fetch counter data:', error);
              setApiError(error);
              resolve({
                regularDayCount: 0, regularDayCountOut: 0, currentCount: 0,
                currentTimestamp: null, doorData: [], sensorData: {}, isDoorSensorActive: false
              });
            } else if (data) {
              const totalData = data.totalData || [];
              const sensorData = data.sensorData || {};

              const latestTotal = totalData.length > 0
                ? totalData[totalData.length - 1]
                : { count: 0, countOut: 0, timestamp: null };

              resolve({
                currentCount: latestTotal.count || 0,
                currentTimestamp: latestTotal.timestamp || null,
                doorData: totalData,     // aggregated time-series (overview chart)
                sensorData: sensorData,  // per-sensor time-series (door cards + detail)
                isDoorSensorActive: true,
                regularDayCount: latestTotal.count || 0,
                regularDayCountOut: latestTotal.countOut || 0
              });
            } else {
              resolve({
                regularDayCount: 0, regularDayCountOut: 0, currentCount: 0,
                currentTimestamp: null, doorData: [], sensorData: {}, isDoorSensorActive: false
              });
            }
          },
          authCtx.sessionToken
        );
      });
    } catch (error) {
      console.error('Error in fetchLocationData:', error);
      setApiError(error);
      return {
        regularDayCount: 0, regularDayCountOut: 0, currentCount: 0,
        currentTimestamp: null, doorData: [], sensorData: {}, isDoorSensorActive: false
      };
    }
  }, [authCtx, navigate, selectedDate, locationData, locationId]);

  // Fetch all-time overview payload (dailySeries / perDoor / perDoorDaily).
  // startDate is calendarMinDate; the server computes endDate off the venue tz.
  const fetchOverviewData = useCallback(async () => {
    return new Promise((resolve) => {
      getQueuesAPI("get-enterprise-office-overview-data",
        {
          timezone: currentLocation.timeZone,
          org: authCtx.org,
          locationId: currentLocation.id,
          measurement: currentLocation.measurement,
          doorMeasurement: currentLocation.doorMeasurement,
          doorCountSensorIds: currentLocation.doorCounterSensorIds,
          doorCounterSensorInfo: currentLocation.doorCounterSensorInfo,
          startDate: currentLocation.calendarMinDate,
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
            }
            console.error('Failed to fetch overview data:', error);
            setApiError(error);
            resolve(null);
          } else if (data) {
            resolve(data);
          } else {
            resolve(null);
          }
        },
        authCtx.sessionToken
      );
    });
  }, [authCtx, navigate, currentLocation]);

  // Initial data fetch (daily)
  useEffect(() => {
    const loadInitialData = async () => {
      if (!authCtx.loggedIn) {
        navigate('/auth');
        return;
      }
      if (!selectedDate) return;

      setLoading(true);
      const data = await fetchLocationData(selectedDate);

      setRegularDayCount(data.regularDayCount);
      setRegularDayCountOut(data.regularDayCountOut);
      setCurrentCount(data.currentCount);
      setCurrentTimestamp(data.currentTimestamp);
      setDoorData(data.doorData);
      setSensorData(data.sensorData);
      setIsDoorSensorActive(data.isDoorSensorActive);

      setLoading(false);
    };

    if (authCtx && (locationData || locationId) && selectedDate) {
      loadInitialData();
    }
  }, [fetchLocationData, selectedDate, authCtx, locationData, locationId, navigate]);

  // Lazily fetch overview data the first time overview mode is entered.
  useEffect(() => {
    if (viewMode !== 'overview') return;
    if (!authCtx.loggedIn) { navigate('/auth'); return; }
    if (overviewData) return; // already loaded — no refetch on toggle back-and-forth

    const load = async () => {
      setOverviewLoading(true);
      const data = await fetchOverviewData();
      setOverviewData(data);
      setOverviewLoading(false);
    };
    load();
  }, [viewMode, authCtx, overviewData, fetchOverviewData, navigate]);

  // Auto-refresh (only for today AND only in regular mode)
  useEffect(() => {
    let interval;

    if (viewMode === 'regular' && autoFetchEnabled && isToday && authCtx && (locationData || locationId) && selectedDate) {
      interval = setInterval(async () => {
        const data = await fetchLocationData(selectedDate);
        setRegularDayCount(data.regularDayCount);
        setRegularDayCountOut(data.regularDayCountOut);
        setCurrentCount(data.currentCount);
        setCurrentTimestamp(data.currentTimestamp);
        setDoorData(data.doorData);
        setSensorData(data.sensorData);
        setIsDoorSensorActive(data.isDoorSensorActive);
      }, 30000);
    }

    return () => { if (interval) clearInterval(interval); };
  }, [viewMode, autoFetchEnabled, isToday, fetchLocationData, selectedDate, authCtx, locationData, locationId]);

  // Manual refresh
  const refreshData = useCallback(async () => {
    if (!authCtx || (!locationData && !locationId) || !selectedDate) {
      console.warn('Missing required dependencies for refresh');
      return;
    }

    setLoading(true);
    const data = await fetchLocationData(selectedDate);
    setRegularDayCount(data.regularDayCount);
    setRegularDayCountOut(data.regularDayCountOut);
    setCurrentCount(data.currentCount);
    setCurrentTimestamp(data.currentTimestamp);
    setDoorData(data.doorData);
    setSensorData(data.sensorData);
    setIsDoorSensorActive(data.isDoorSensorActive);
    setLoading(false);
  }, [fetchLocationData, selectedDate, authCtx, locationData, locationId]);

  return children({
    loading,
    regularDayCount,
    regularDayCountOut,
    currentCount,
    currentTimestamp,
    doorData,
    sensorData,
    isDoorSensorActive,
    isToday,
    autoFetchEnabled,
    setAutoFetchEnabled,
    refreshData,
    authCtx,
    locationData,
    locationId,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
    enabledDates,
    showCalendar,
    setShowCalendar,
    apiError,
    // overview
    viewMode,
    setViewMode,
    overviewData,
    overviewLoading,
  });
}